/**
 * React side of the quote markup (decision 107): <QuoteMarkup/> renders the
 * shared parser's blocks, <MarkupToolbar/> is the B / I / U / bullets bar the
 * cover-note and footer textareas wear in the send slide-over.
 *
 * The toolbar WRITES the plain-text markers (**…**, _…_, __…__, "- ") into the
 * textarea — the stored value stays an ordinary string, so snippets, tokens
 * and templates are untouched.
 */
import { Fragment } from 'react'
import { parseQuoteMarkup, toggleBullets, toggleWrap, type MarkupRun, type ToolbarResult } from '@shared/quoteMarkup'

function Run({ r }: { r: MarkupRun }) {
  let node: React.ReactNode = r.text
  if (r.bold) node = <strong>{node}</strong>
  if (r.italic) node = <em>{node}</em>
  if (r.underline) node = <u>{node}</u>
  return <>{node}</>
}

const Line = ({ runs }: { runs: MarkupRun[] }) =>
  <>{runs.map((r, i) => <Run key={i} r={r} />)}</>

/** Render cover-note / footer text with its markup. Empty text renders nothing. */
export function QuoteMarkup({ text }: { text: string }) {
  const blocks = parseQuoteMarkup(text)
  if (blocks.length === 0) return null
  return (
    <div className="qmarkup">
      {blocks.map((b, i) =>
        b.kind === 'p'
          ? (
              <p key={i}>
                {b.lines.map((l, j) => (
                  <Fragment key={j}>{j > 0 && <br />}<Line runs={l} /></Fragment>
                ))}
              </p>
            )
          : (
              <ul key={i}>
                {b.items.map((item, j) => <li key={j}><Line runs={item} /></li>)}
              </ul>
            ))}
    </div>
  )
}

/**
 * The toolbar itself. `target` is the textarea it operates on; `onApply`
 * receives the updated full text (the caller owns the state).
 */
export function MarkupToolbar({ target, disabled, onApply }: {
  target: () => HTMLTextAreaElement | null
  disabled?: boolean
  onApply: (text: string) => void
}) {
  const apply = (fn: (text: string, s: number, e: number) => ToolbarResult) => {
    const el = target()
    if (!el) return
    const r = fn(el.value, el.selectionStart ?? 0, el.selectionEnd ?? 0)
    onApply(r.text)
    // Restore focus + selection after React re-renders the value.
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(r.selStart, r.selEnd)
    })
  }
  return (
    <span className="mtoolbar" role="toolbar" aria-label="Formatting">
      <button type="button" className="btn btn--sm btn--ghost" style={{ fontWeight: 700 }} title="Bold (**text**)"
        disabled={disabled} onClick={() => apply((t, s, e) => toggleWrap(t, s, e, '**'))}>B</button>
      <button type="button" className="btn btn--sm btn--ghost" style={{ fontStyle: 'italic' }} title="Italic (_text_)"
        disabled={disabled} onClick={() => apply((t, s, e) => toggleWrap(t, s, e, '_'))}>I</button>
      <button type="button" className="btn btn--sm btn--ghost" style={{ textDecoration: 'underline' }} title="Underline (__text__)"
        disabled={disabled} onClick={() => apply((t, s, e) => toggleWrap(t, s, e, '__'))}>U</button>
      <button type="button" className="btn btn--sm btn--ghost" title={'Bullet list ("- " lines)'}
        disabled={disabled} onClick={() => apply(toggleBullets)}>• list</button>
    </span>
  )
}
